import React, { ReactNode, useEffect, useRef } from 'react'
import clsx from 'clsx'
import { InputBaseProps, MenuItem, Typography } from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/Error'
import { Tooltip, Spinner, TextField, TextFieldAdornmentPrefix, TextFieldAdornmentSuffix, BooleanField, SelectField, RichTextField, RichTextFieldToolbarType, RichTextFieldMaxSize, ButtonField, ImageField, TagField, DateField } from 'components'
import { makeStyles } from '@material-ui/core'
import { Color, FontSize, FontWeight, Value } from 'theme/style'
import { renderHtml } from 'app/utils'
import spacing from 'theme/config/spacing'
import _ from 'lodash';
import { config } from 'app/params'
import DateRangeIcon from '@material-ui/icons/DateRange'
import InfoIcon from '@material-ui/icons/Info'
import { lighten } from '@material-ui/core/styles'
import { useLocalization } from 'components/methods'
import LockIcon from '@material-ui/icons/Lock'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      marginBottom: spacing(1.5),
    },
  },
  containerBoolean: {
    flex: '0 0 70px'
  },
  containerHidden: {
    display: 'none'
  },
  infoContainer: {
    position: 'relative',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },
  infoContainerVertical: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& >p': {
      marginBottom: theme.spacing(0.5),
    }
  },
  infoContainerHorizontal: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    fontWeight: FontWeight.SemiBold,
    fontSize: FontSize.Body2,
    display: 'flex',
    alignItems: 'center',
    minHeight: '26px'
  },
  labelError: {
    //color: Color.Error
  },
  mandatory: {
    marginLeft: '3px',
    color: Color.Error
  },
  descriptionContainer: {
    marginTop: theme.spacing(1)
  },
  description: {
    color: Color.TextLight,
    fontSize: FontSize.BodySmall,
  },
  fieldContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    minHeight: '50px',
    height: '50px',
    width: '100%',
    flex: 1,
    position: 'relative',
    borderRadius: Value.BorderRadius_Field,
    overflow: 'hidden',
    '& >div': {
      minHeight: '50px',
      width: '100%',
      justifyContent: 'flex-start',
      '& >div': {
        display: 'flex',
        alignItems: 'center',
        minHeight: '50px',
      }
    }
  },
  fieldContainerSmall: {
    minHeight: '36px',
    height: '36px',
    borderRadius: '18px',
    '& >div': {
      minHeight: '36px',
      '& >div': {
        minHeight: '36px',
      }
    }
  },
  fieldContainerLeftAligned: {
    justifyContent: 'flex-start',
  },

  startIconContainer: {
    color: Color.TextPrimary,
    width: '45px !important',
    height: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center !important',
    left: 0,
    top: 0,
    zIndex: 999,
    '& > svg': {
      height: '20px',
      width: '20px',
      color: Color.TextSecondary
    }
  },
  endIconContainer: {
    color: Color.TextPrimary,
    backgroundColor: Color.Background,
    width: '45px !important',
    height: '45px !important',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center !important',
    right: 0,
    top: 0,
    zIndex: 999,
  },
  disabledIcon: {
    pointerEvents: 'none'
  },
  errorIconContainer: {
    width: '2rem',
    height: '2rem',
    minHeight: '2rem !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      width: '5rem',
      height: '5rem',
    },

  },
  errorIcon: {
    color: Color.Error,
    width: '1.7rem',
    height: '1.7rem',
  },
  lockIcon: {
    color: lighten(Color.TextSecondary, 0.4),
    width: '1.7rem',
    height: '1.7rem',
    marginRight: theme.spacing(0.5),
    cursor: 'pointer'
  },
  helpIconContainer: {
    width: '1.5rem',
    height: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '0.5rem',
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      width: '4rem',
      height: '4rem',
      marginLeft: '-0.5rem',
    },
  },
  helpIcon: {
    color: lighten(Color.TextSecondary, 0.4),
    width: '1.5rem',
    height: '1.5rem',
    cursor: 'pointer',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    height: '100%',
    top: 0,
    left: 0,
    width: '100%',
  },

  inputTextFieldLeftPadding: {
    '& input, & textarea': {
      paddingLeft: '30px !important'
    }
  },
  inputTextFieldRightPadding: {
    '& input, & textarea': {
      paddingRight: '45px !important'
    }
  },

  richTextBig: {
    maxHeight: '70vh'
  },

  option: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  optionTitle: {
  },
  optionDescription: {
    color: Color.TextLight,
    fontSize: FontSize.BodyMini
  }
}))

export type FieldError = {
  error: boolean
  errorMessage?: string
}

export type FieldsListErrors = { [key: string]: FieldError[] }


interface CardFieldProps extends InputBaseProps {
  id?: string
  className?: string
  isLoading?: boolean
  layout?: 'vertical' | 'horizontal'
  disabled?: boolean
  hidden?: boolean
  mandatory?: boolean
  size?: 'small' | 'default'

  //The type of each field supported.
  type?: 'text' | 'textarea' | 'password' | 'email' | 'number' | 'select' | 'boolean' | 'richtext' | 'button' | 'image' | 'tags' | 'date'

  //Defines if the field should autosave when changed.
  autosave?: boolean

  contentData?: any 

  //Common props.
  name: string
  label?: string | ReactNode
  placeholder?: string
  description?: string
  helpDescription?: string
  value?: any
  errors?: FieldsListErrors
  validation?: (value: string | null) => boolean

  //Common methods.
  //Triggered when the field changed value.
  onUpdate?: (name: string, value: any, action?: string, object?: any) => void

  //Triggered when the field confirmed the change (or when autosaved).
  onConfirm?: (name: string, value: any) => void

  //Triggered when the field has been clicked (used on specific types such as Button and Image).
  onClick?: () => void

  //Triggered when the field has been removed its value (used on specific types such as Button and Image).
  onRemove?: () => void

  prevent?: string[]
  enableClear?: boolean

  //Defines icons placed at the end of the field.
  startIcon?: ReactNode
  endIcon?: ReactNode

  //Defines a prefix and a suffix (used in almost every field type).
  prefix?: string
  suffix?: string

  //Type Text specific props
  enableEnterConfirm?: boolean

  //Type Select specific props
  options?: { key?: string, value: number | string, name: string, description?: string, className?: string }[]
  clearText?: string
  async?: boolean

  //Type Date specific props
  minDate?: Date
  maxDate?: Date
  labelFunc?: (date: any, invalidLabel: string) => string

  //Type Button spcific props
  enableEdit?: boolean
  enableUpload?: boolean

  //Type Tags specific props
  items?: any[]
  activeItems?: any[]
  itemValueConstructor?: (item: any) => string
  itemRenderClass?: (item: any) => string

  //Type RichText specific props
  richTextToolbarType?: RichTextFieldToolbarType
  richTextMaxSize?: RichTextFieldMaxSize
}

const CardField = ({ ...props }: CardFieldProps) => {
  const classes = useStyles()
  const { t } = useLocalization()



  //DEFAULTS

  let layoutClass = clsx(classes.infoContainer, classes.infoContainerVertical)
  if (props.layout === 'horizontal') layoutClass = clsx(classes.infoContainer, classes.infoContainerHorizontal)
  if (props.layout === 'vertical') layoutClass = clsx(classes.infoContainer, classes.infoContainerVertical)



  //AUTOSAVE

  const autosaveDefaultValue = true
  const autosaveValue = props.autosave ?? autosaveDefaultValue

  //Automatically triggers the search confirm after a delay while typing.
  var timer: NodeJS.Timeout
  const data: any = useRef(null)
  data.current = props.onConfirm

  useEffect(() => {
    if (autosaveValue === false) return
    if (props.onConfirm == null) return
    if (props.name == null) return

    timer = setTimeout(() => {
      if (timer) clearTimeout(timer)
      data.current(props.name, props.value)
    }, config.automation.autosave.delay)
    return () => clearTimeout(timer)
  }, [props.value])



  //FIELD UPDATES

  function updateField(name: string, value: any, action?: string, object?: any) {
    if (props.onUpdate == null) return
    if (props.validation != null) {
      if (props.validation(value) === false) return
    }
    
    props.onUpdate(name, value, action, object)
  }

  function confirmField(name: string, value: any) {
    if (props.onConfirm == null) return
    props.onConfirm(name, value)
  }



  //RENDER

  function renderField(): ReactNode {

    if (props.isLoading != null && props.isLoading === true) return <div className={classes.loading}><Spinner type={'small'} /></div>
    return render()

    function render(): ReactNode {
      if (props.type == null) return <></>

      //Defines all common props passed to the different field types.
      let placeholder = props.placeholder
      if (placeholder == null && props.id != null) placeholder = t(props.id + '.placeholder')
      if (props.contentData?.placeholder != null) placeholder = props.contentData?.placeholder

      let allProps: any = {
        name: props.name,
        placeholder: placeholder,
        value: props.value,
        disabled: props.disabled,
        onUpdate: updateField,
        onConfirm: confirmField,
      }
      if (props.prefix != null) {
        const adornment = <TextFieldAdornmentPrefix text={props.prefix} />
        allProps['startAdornment'] = adornment
      }
      if (props.suffix != null) {
        const adornment = <TextFieldAdornmentSuffix text={props.suffix} />
        allProps['endAdornment'] = adornment
      }
      if (props.prevent != null) {
        const onKeyDown = (e:any) => props.prevent?.includes(e.key) && e.preventDefault()
        allProps['onKeyDown'] = onKeyDown
      }

      let fieldStartIconClass = ''
      if (props.startIcon != null || props.type === 'date') {
        fieldStartIconClass = classes.inputTextFieldLeftPadding
      }

      let fieldEndIconClass = ''
      if (props.endIcon != null) {
        fieldEndIconClass = classes.inputTextFieldRightPadding
      }

      //Renders the "Text" type.
      if (props.type === 'text') return <TextField {...allProps} className={clsx(fieldStartIconClass, fieldEndIconClass)} enableEnterConfirm={props.enableEnterConfirm} />

      //Renders the "Textarea" type.
      if (props.type === 'textarea') return <TextField {...allProps} className={clsx(fieldStartIconClass, fieldEndIconClass)} multiline />

      //Renders the "Password" type.
      if (props.type === 'password') return <TextField {...allProps} type={'password'} className={clsx(fieldStartIconClass, fieldEndIconClass)}
      />

      //Renders the "Number" type.
      if (props.type === 'number') return <TextField {...allProps} type={'number'} className={clsx(fieldStartIconClass, fieldEndIconClass)}/>

      //Renders the "Boolean" type.
      if (props.type === 'boolean') return <BooleanField {...allProps} />

      //Renders the "Rich Text" type.
      if (props.type === 'richtext') return <RichTextField {...allProps} toolbarType={props.richTextToolbarType} maxSize={props.richTextMaxSize} />

      //Renders the "Button" type.
      if (props.type === 'button') return <ButtonField {...allProps} toolbarType={props.richTextToolbarType} enableEdit={props.enableEdit} enableClear={props.enableClear} enableUpload={props.enableUpload} onClick={props.onClick} onRemove={props.onRemove} />

      //Renders the "Image" type.
      if (props.type === 'image') return <ImageField {...allProps} onClick={props.onClick} onRemove={props.onRemove} />

      //Renders the "Tags" type.
      if (props.type === 'tags') return <TagField {...allProps} items={props.items ?? []} activeItems={props.activeItems ?? []} itemValueConstructor={props.itemValueConstructor} itemRenderClass={props.itemRenderClass} />

      //Renders the "Date" type.
      if (props.type === 'date') return <DateField {...allProps} className={clsx(fieldStartIconClass, fieldEndIconClass)} minDate={props.minDate} maxDate={props.maxDate} labelFunc={props.labelFunc} />

      //Renders the "Select" type.
      if (props.type === 'select') return <SelectField {...allProps} async={props.async} enableClear={props.enableClear} clearText={props.clearText} >
        {(props.async === false || props.async == null) && props.options?.map((o) => (
          <MenuItem className={clsx(classes.option, o.className)} key={o.key ?? String(o.value)} value={o.value}>
            <div className={classes.optionTitle}>{renderHtml(o.name)}</div>
            {o.description != null && <div className={classes.optionDescription}>{o.description}</div>}
          </MenuItem>
        ))}
      </SelectField>
    }
  }

  function renderIcon(type: 'start' | 'end'): ReactNode {
    if (type === 'start') return renderStartIcon()
    if (type === 'end') return renderEndIcon()
    return <></>

    function renderStartIcon(): ReactNode {
      if (props.type === 'date') return <div className={clsx(classes.startIconContainer, classes.disabledIcon)}><DateRangeIcon /></div>
      if (props.startIcon == null) return <></>
      return <div className={classes.startIconContainer}>{props.startIcon}</div>
    }
    function renderEndIcon(): ReactNode {
      let endIcon: React.ReactNode | null = null
      if (props.disabled === true) endIcon = <Tooltip title={t('GEL.whitelabel.component.CardField.disabled.tooltip')} placement="top"><LockIcon className={classes.lockIcon} /></Tooltip>
      if (props.endIcon != null) endIcon = props.endIcon
      if (hasError() === true) endIcon = renderErrorIcon()
      if (endIcon != null) return <div className={classes.endIconContainer}>{endIcon}</div>
      return <></>
    }
  }

  function hasError(): boolean {
    if (props.errors == null) return false
    if (props.value === '') return false

    const errorList = props.errors[props.name]
    const errorFields = _.find(errorList, 'error')
    return errorFields != null
  }

  function renderErrorIcon(): ReactNode | null {
    if (props.errors == null) return null
    if (props.value == null) return null

    let errorNodes: ReactNode[] = []
    const errorList = props.errors[props.name]
    const validErrors = errorList.filter(e => e.error === true)
    const hasMultiple = validErrors.length > 1
    validErrors.forEach(e => {
      if (e.error === true && e.errorMessage != null) errorNodes.push(<div>{hasMultiple === true && '• '}{e.errorMessage}</div>)
    })
    if (errorNodes.length === 0) return <></>
    return <Tooltip title={<>{errorNodes.map(e => e)}</>} placement="top"><div className={classes.errorIconContainer}><ErrorIcon className={classes.errorIcon} /></div></Tooltip>
  }

  let fieldContainerSizeClass = ''
  if (props.size === 'small') fieldContainerSizeClass = classes.fieldContainerSmall

  let containerClass = ''
  if (props.type === 'boolean') containerClass = classes.containerBoolean
  if (props.hidden === true) containerClass = clsx(containerClass, classes.containerHidden)

  let label = props.label
  if (label == null && props.id != null) label = t(props.id + '.label')
  if (props.contentData?.label != null) label = props.contentData?.label

  let helpDescription = props.helpDescription
  if (helpDescription == null && props.id != null) helpDescription = t(props.id + '.help-description')
  if (props.contentData?.helpDescription != null) helpDescription = props.contentData?.helpDescription

  let description = props.description
  if (description == null && props.id != null) description = t(props.id + '.description')
  if (props.contentData?.description != null) description = props.contentData?.description

  return (
    <div className={clsx(props.className, classes.container, containerClass)}>
      <div className={layoutClass}>
        {(props.label != null || props.id != null) && <Typography className={hasError() === true ? clsx(classes.label, classes.labelError) : classes.label} variant="body1">
          {(label != null) && <span>{label}</span>}
          {(props.mandatory === true) && <span className={classes.mandatory}>*</span>}
          {(helpDescription != null && helpDescription.length > 0) && <Tooltip title={''} htmlTitle={helpDescription} placement="top"><div className={classes.helpIconContainer}><InfoIcon className={classes.helpIcon} /></div></Tooltip>}
        </Typography>}
        <div className={clsx(classes.fieldContainer, fieldContainerSizeClass)}>
          {renderIcon('start')}
          {renderField()}
          {renderIcon('end')}
        </div>
      </div>
      <div className={clsx(classes.descriptionContainer)}>
        {description != null && (<Typography className={classes.description} variant="body2">{description}</Typography>)}
      </div>
    </div >
  )
}

export default CardField
